/**
 * Связь страницы панели (iframe расширения) с миром вокруг:
 * - запросы `/api/…` уходят через service worker расширения — у него cookie сессии кабинета;
 * - с content-скриптом площадки (родительское окно) — `postMessage`: кто открыт, вставить текст,
 *   фото с экрана для поиска похожих, вставить фото с подписью.
 */

import { ApiFailure } from "@/lib/api";

// Типов chrome здесь нет: страница собирается вместе с кабинетом, где их не ставили.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const chrome: any;

export type Peer = {
  ready: boolean;
  id?: string;
  person?: boolean;
  name?: string;
  phone?: string;
  username?: string;
  contact?: boolean;
  /** MAX: id человека (id диалога XOR id аккаунта) — по нему сервер ведёт связку с номером. */
  contact_id?: string;
  /** WhatsApp: цифры id чата без `@lid` — этим id чат из браузера заведён в «Чатах». */
  import_id?: string;
  /** Id своего аккаунта на площадке. */
  account?: string;
  error?: string;
  /** Чем страница сверила диалог с экраном и у каких чатов модель площадки держит флаг зря. */
  check?: { by: string; stale: string[] };
  /** Bitrix24: что открыто — карточка сделки или лида по id, либо звонок с номера без них. */
  entity?: { type: "deal" | "lead" | "phone"; id: string };
  /** Bitrix24: откуда узнали — карточка сбоку, окно звонка или обзвона, адрес страницы. */
  from?: "slider" | "call" | "page";
};

export type PanelState = {
  channel: "telegram" | "max" | "whatsapp" | "bitrix";
  platform: string;
  peer: Peer | null;
  account: { id: string; name: string; phone?: string } | null;
  chatId: number | null;
  version: string;
};

/** Адрес кабинета, где сотрудник вошёл (основной или зеркало prokat-rf), — знает service worker. */
export let cabinet = "https://prokat.unirenter.ru";

/** Диалог (id собеседника), который панель показывает сейчас; ставит `ExtPanel` из состояния. */
let shown = "";
export function setShown(peer: string): void {
  shown = peer;
}

/** Запросы панели, которые диалог не меняют: вход, свой журнал, «кто это», импорт переписки и
 * «Выделить» (сотрудник берёт работу себе — какая карточка открыта, неважно). */
/** Версия расширения — для журнала ошибок prokat. */
export const extVersion = (): string | undefined => chrome.runtime?.getManifest?.()?.version;

const FREE = ["/api/login", "/api/errors", "/api/ext/report", "/api/ext/peer", "/api/ext/entity", "/api/ext/history", "/api/ext/take"];

/**
 * Тот ли диалог открыт на площадке, что показан в панели: content-скрипт сверяется со страницей
 * сразу, не дожидаясь своей секунды. → пусто или причина, почему действие делать нельзя. 03.10 панель
 * пять часов показывала чат, из которого сотрудник ушёл, — ссылка на запись ушла бы чужому клиенту.
 */
async function peerProblem(): Promise<string> {
  const was = shown;
  const r = await ask("verify", {}, "verified", 3000, { peer: null });
  if (r.peer === null) return "Площадка не ответила — обновите вкладку (F5)";
  return r.peer === was ? "" : "Открыт уже другой диалог — панель обновилась. Проверьте клиента и повторите";
}

/**
 * Подменяем fetch для `/api/…`: относительный путь в iframe расширения указывал бы в никуда.
 * Любая запись (ссылка на запись, стадия, дата, город…) — только после сверки диалога (`peerProblem`).
 */
export function proxyFetch(): void {
  chrome.runtime.sendMessage({ type: "base" }).then(
    (r: { base?: string } | null) => {
      if (r?.base) cabinet = r.base;
    },
    () => undefined,
  );
  // Расширение переехало на другое имя кабинета, пока панель открыта: ссылки — на него же.
  chrome.storage.onChanged.addListener((c: { base?: { newValue?: string } }) => {
    if (c.base?.newValue) cabinet = c.base.newValue;
  });
  const native = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const path = typeof input === "string" ? input : input instanceof URL ? input.pathname + input.search : input.url;
    if (!path.startsWith("/api/")) return native(input, init);
    const body = typeof init?.body === "string" ? init.body : undefined;
    if ((init?.method ?? "GET") !== "GET" && !FREE.includes(path.split("?")[0] ?? path)) {
      const problem = await peerProblem();
      if (problem) {
        log("blocked", { path: path.slice(0, 120), reason: problem });
        return new Response(JSON.stringify({ error: [{ code: "PEER_CHANGED", message: problem }], results: null }), {
          status: 409,
          headers: { "Content-Type": "application/json" },
        });
      }
    }
    const started = Date.now();
    const r: { status: number; text?: string; b64?: string; type?: string } = await chrome.runtime.sendMessage({
      type: "fetch",
      method: init?.method ?? "GET",
      path,
      body,
    });
    log("api", { path: path.slice(0, 120), method: init?.method ?? "GET", status: r.status, ms: Date.now() - started });
    // Не-JSON (фото каталога) service worker отдаёт base64.
    if (r.b64 !== undefined)
      return new Response(Uint8Array.from(atob(r.b64), (c) => c.charCodeAt(0)), {
        status: r.status || 503,
        headers: { "Content-Type": r.type ?? "application/octet-stream" },
      });
    return new Response(r.text ?? "", { status: r.status || 503, headers: { "Content-Type": "application/json" } });
  };
}

/**
 * prokat недоступен: перезапуск `prokat-web` — это 6–9 с ответов 502 от шлюза, нет сети — 503 выше.
 * Такой запрос панель повторяет сама: 05.10 вкладка, загрузившаяся в эту паузу, показывала
 * «prokat не ответил» до F5.
 */
export const down = (e: unknown): boolean => e instanceof ApiFailure && [502, 503, 504].includes(e.status);

const parentOrigin = () => window.location.ancestorOrigins?.[0] ?? "*";

export function toParent(msg: Record<string, unknown>): void {
  window.parent.postMessage({ prokatPanel: true, ...msg }, parentOrigin());
}

/**
 * Событие панели — в журнал действий вкладки (его ведёт content-скрипт): что панель показала, что
 * ей ответил prokat. Журнал уходит в prokat только с сообщением о баге (кнопка шапки рамки).
 */
export function log(event: string, data: Record<string, unknown> = {}): void {
  toParent({ kind: "log", event, data });
}

export function onParent(handler: (msg: Record<string, unknown>) => void): () => void {
  const listener = (e: MessageEvent) => {
    if (e.source !== window.parent || !e.data || e.data.prokatHost !== true) return;
    handler(e.data as Record<string, unknown>);
  };
  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
}

let seq = 0;
/** Запрос content-скрипту и его ответ того же `id`; не ответил за `ms` — `fallback`. */
function ask(kind: string, extra: Record<string, unknown>, reply: string, ms: number, fallback: Record<string, unknown>) {
  const id = ++seq;
  return new Promise<Record<string, unknown>>((ok) => {
    const off = onParent((m) => {
      if (m.kind === reply && m.id === id) {
        off();
        ok(m);
      }
    });
    toParent({ kind, id, ...extra });
    setTimeout(() => (off(), ok(fallback)), ms);
  });
}

/**
 * Попросить content-скрипт вставить текст в поле сообщения; ответ — пустая строка или беда.
 * `peer` — диалог, для которого текст готовили: открыт уже другой — content-скрипт не вставит.
 */
export async function insertText(text: string, peer: string): Promise<string> {
  const r = await ask("insert", { text, peer }, "inserted", 8000, { error: "площадка не ответила" });
  return String(r.error ?? "");
}

/**
 * Открыть на площадке диалог по номеру (форма «+»): `link` — как по ссылке wa.me, `contact` —
 * сначала завести номер в контакты аккаунта. `note` — что вышло попутно («уже в контактах»).
 * С `peer_id` (окно «Ждут ответа»: собеседник уже известен) Telegram и MAX открывают диалог по
 * нему, без поиска по номеру; WhatsApp — по номеру, как всегда.
 */
export async function openChat(to: {
  phone: string;
  how: "link" | "contact";
  peer_id?: string;
  username?: string;
}): Promise<{ error: string; note: string }> {
  const r = await ask("open", to, "opened", 70000, { error: "площадка не ответила — обновите вкладку (F5)" });
  return { error: String(r.error ?? ""), note: String(r.note ?? "") };
}

/** Фото, открытое на экране площадки, — JPEG до 640 px в base64; или причина, почему нет. */
export async function screenPhoto(): Promise<{ data: string | undefined; error: string | undefined }> {
  const r = await ask("photo", {}, "photo", 20000, { error: "площадка не ответила" });
  return { data: r.data as string | undefined, error: r.error as string | undefined };
}

/**
 * Фото в окно отправки площадки и `text` в подпись. `files: false` — окна отправки не было (MAX
 * синтетическую вставку файлов не принимает): в поле сообщения встал `textLink`, фото — нет.
 */
export async function insertFiles(
  files: { name: string; blob: Blob }[],
  text: string,
  textLink: string,
  peer: string,
): Promise<{ error: string; files: boolean }> {
  const r = await ask("insertFiles", { files, text, textLink, peer }, "inserted", 20000, { error: "площадка не ответила", files: false });
  return { error: String(r.error ?? ""), files: !!r.files };
}

export const storage = {
  get: async (key: string): Promise<string> => ((await chrome.storage.local.get(key)) as Record<string, string>)[key] ?? "",
  set: (key: string, value: string): Promise<void> => chrome.storage.local.set({ [key]: value }),
};

export const version = (): string => chrome.runtime.getManifest().version as string;

/**
 * Service worker помнит ответы опросов («ждут ответа», вход, справочники) на все вкладки сотрудника.
 * `flush` просит спросить prokat заново: без `all` — только числа «ждут ответа» и «Выделить».
 */
export const flush = (all = false): Promise<void> =>
  (chrome.runtime.sendMessage({ type: "flush", all }) as Promise<unknown>).then(
    () => undefined,
    () => undefined,
  );
